import type {Boundary} from "../../../data/models/Boundary";
import type {BoundarySpaceMap} from "../../../data/models/BoundarySpaceMap";
import {BoundaryUtils} from "../../../data/models/BoundaryUtils";
import type {Catalog} from "../../../data/models/Catalog";
import type {DocumentModel} from "../../../data/models/DocumentModel";
import type {Link} from "../../../data/models/Link";
import type {Markup} from "../../../data/models/Markup";
import type {IModel} from "../../../data/models/Model";
import type {Portfolio} from "../../../data/models/Portfolio";
import type {Space} from "../../../data/models/Space";
import type {Xyicon} from "../../../data/models/Xyicon";
import {FeatureService} from "../../../data/services/FeatureService";
import type {AppState} from "../../../data/state/AppState";
import {NavigationEnum} from "../../../Enums";
import {Permission} from "../../../generated/api/base";
import type {
	BoundaryDto,
	DocumentDto,
	FullDocumentDto,
	MarkupDto,
	ObjectScopedLinkDto,
	PortfolioDto,
	SpaceDto,
	XyiconCatalogDto,
	XyiconDto,
} from "../../../generated/api/base";
import {XyiconFeature} from "../../../generated/api/reports";
import type {OrgSearchResponseDto} from "../../../generated/api/search";
import {useAppStore} from "../../../StateManager";
import {ArrayUtils} from "../../../utils/data/array/ArrayUtils";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {MathUtils} from "../../../utils/math/MathUtils";
import type {Markup3D} from "../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/Markup3D";
import type {MarkupTypeName} from "../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/MarkupTypeNameUtils";
import {MarkupTypenameToType} from "../../modules/space/spaceeditor/logic3d/elements3d/markups/abstract/MarkupTypeNameUtils";

export type ScopeWideSearchType = "Portfolio" | "Organization";

const syncOrgSearchResponseToModelDto = (
	response: OrgSearchResponseDto,
): XyiconDto | BoundaryDto | MarkupDto | SpaceDto | XyiconCatalogDto | PortfolioDto | DocumentDto => {
	switch (response.feature) {
		case XyiconFeature.Xyicon:
			(response as XyiconDto).xyiconID = response.id;
			(response as XyiconDto).xyiconRefID = response.refID;
			break;
		case XyiconFeature.Boundary:
			(response as BoundaryDto).boundaryID = response.id;
			(response as BoundaryDto).boundaryRefID = response.refID;
			break;
		case XyiconFeature.Markup:
			// Markups response now contains the V5 type names. We want to map the correct markup type for that type name.
			(response as MarkupDto).type = MarkupTypenameToType[response.markupType as MarkupTypeName];
			(response as MarkupDto).markupID = response.id;
			(response as MarkupDto).markupRefID = response.refID;
			break;
		case XyiconFeature.Space:
			(response as SpaceDto).spaceID = response.id;
			(response as SpaceDto).spaceRefID = response.refID;
			(response as SpaceDto).name = response.spaceName;
			break;
		case XyiconFeature.XyiconCatalog:
			(response as XyiconCatalogDto).xyiconCatalogID = response.id;
			(response as XyiconCatalogDto).xyiconCatalogRefID = response.refID;
			break;
		case XyiconFeature.Portfolio:
			(response as PortfolioDto).portfolioID = response.id;
			(response as PortfolioDto).portfolioRefID = response.refID;
			(response as PortfolioDto).name = response.portfolioName;
			break;
		case XyiconFeature.Document:
		case XyiconFeature.OrganizationDocument:
		case XyiconFeature.PortfolioDocument:
			(response as DocumentDto).documentID = response.id;
			if (!(response as FullDocumentDto).documentMap) {
				console.log(`Document map is missing for document: ${response.id}`);
				(response as FullDocumentDto).documentMap = [];
			}

			break;
	}

	return response;
};

export const createLocalModelFromOrgSearchResponse = (appState: AppState, response: OrgSearchResponseDto): IModel => {
	const syncedDto = syncOrgSearchResponseToModelDto(response);

	let feature: XyiconFeature = response.feature;
	if (response.feature === XyiconFeature.Document) {
		if (response.portfolioID) {
			feature = XyiconFeature.PortfolioDocument;
		} else {
			feature = XyiconFeature.OrganizationDocument;
		}
	}

	const localModelMaybe: IModel = appState.actions.getFeatureItemById(response.id, feature);
	const model = FeatureService.createModel({...syncedDto, ...(localModelMaybe?.data ?? {})}, feature, appState);

	return model as Xyicon | Boundary | Markup | Space | Catalog | Portfolio | DocumentModel;
};

export const getOrgSearchModelsByIds = async (appState: AppState, ids: string[], extendWithDetails: boolean = true): Promise<IModel[]> => {
	const {transport} = appState.app;
	const authData = transport.services.auth.authData;

	const detailsRequestParams = {
		objectIDs: ids,
	};

	const queryParams = XHRLoader.encodeParams(detailsRequestParams as unknown as Record<string, string | string[]>);

	const response = await fetch(`${transport.config.api.search}/details?${queryParams}`, {
		method: XHRLoader.METHOD_GET,
		headers: {
			Authorization: `${StringUtils.capitalize(authData.tokenType)} ${authData.accessToken}`,
		},
	});

	const details = (await response.json()) as OrgSearchResponseDto[];
	let models: IModel[] = [];

	for (const d of details) {
		let model = createLocalModelFromOrgSearchResponse(appState, d);
		if (extendWithDetails) {
			model = await extendLocalOrgSearchModel(appState, model);
		}

		models.push(model);
	}

	return models;
};

/**
 * Organization-wide search results can be separate model instances carrying search-only
 * data. Resolve a result to the instance kept by AppState before using it outside the search
 * UI. This keeps details edits and SignalR updates on the same object.
 */
export const resolveLocalOrgSearchModel = (appState: AppState, model: IModel): IModel => {
	const existingModelMaybe = appState.actions.getFeatureItemById(model.id, model.ownFeature);

	if (existingModelMaybe) {
		if (existingModelMaybe !== model) {
			// Keep the newest local state while retaining data only available on the search result.
			existingModelMaybe.applyData({...(model.data ?? {}), ...(existingModelMaybe.data ?? {})});
		}

		return existingModelMaybe;
	}

	const lists = model.portfolioId === appState.activePortfolioId ? appState.lists : appState.orgWideLists;
	lists[model.ownFeature].add(model);

	return model;
};

export const extendLocalOrgSearchModel = async (appState: AppState, model: IModel): Promise<IModel> => {
	const {transport} = appState.app;
	const {actions} = appState;

	model = resolveLocalOrgSearchModel(appState, model);

	if (
		[XyiconFeature.Portfolio, XyiconFeature.Boundary, XyiconFeature.Xyicon, XyiconFeature.Space, XyiconFeature.XyiconCatalog].includes(
			model.ownFeature,
		)
	) {
		const {result, error} = await transport.requestForOrganization<FullDocumentDto[]>({
			url: "documents/object/all",
			method: "GET",
			json: true,
			params: {
				id: model.id,
				feature: model.ownFeature,
			},
		});

		if (result?.length > 0) {
			for (const docDto of result) {
				const existingDocWithSameIdMaybe = actions.getDocumentById(docDto.documentID);
				if (existingDocWithSameIdMaybe) {
					existingDocWithSameIdMaybe.applyData(docDto);
				} else {
					const newDocumentModel = FeatureService.createModel(docDto, XyiconFeature.PortfolioDocument, appState) as DocumentModel;
					appState.lists[XyiconFeature.PortfolioDocument].add(newDocumentModel);
				}
			}
		}
	}

	if (model.ownFeature === XyiconFeature.Portfolio) {
		const spacesMaybe = await transport.requestForOrganization<SpaceDto[]>({
			url: "spaces/all",
			method: "GET",
			json: true,
			params: {
				portfolioID: model.id,
			},
		});
		if (MathUtils.isValidNumber(spacesMaybe.result?.length)) {
			const filteredSpaces = spacesMaybe.result.filter((spaceDto: SpaceDto) => {
				const perm = actions.getModuleTypePermission(spaceDto.spaceTypeID, XyiconFeature.Space, model.id);

				return perm > Permission.None;
			});
			(model as Portfolio).applyData({spaces: filteredSpaces});
		}
	} else if ([XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(model.ownFeature)) {
		const linksMaybe = await transport.request<ObjectScopedLinkDto[]>({
			url: "links/object/all",
			method: "GET",
			json: true,
			params: {
				id: model.id,
				feature: model.ownFeature,
			},
		});

		if (linksMaybe.result?.length > 0) {
			const idsForDetails: string[] = [];
			for (const linkData of linksMaybe.result) {
				let linkObj: Link;
				const existingLinkMaybe = appState.actions.getFeatureItemById<Link>(linkData.linkID, XyiconFeature.Link);
				if (existingLinkMaybe) {
					existingLinkMaybe.applyData(linkData);
					linkObj = existingLinkMaybe;
				} else {
					linkObj = appState.actions.addToList(linkData, XyiconFeature.Link) as Link;
				}

				const otherModelId =
					(linkObj.objectABoundaryID ?? linkObj.objectAID) === model.id
						? (linkObj.objectBBoundaryID ?? linkObj.objectBID)
						: (linkObj.objectABoundaryID ?? linkObj.objectAID);
				idsForDetails.push(otherModelId);
			}

			const features: XyiconFeature[] = [XyiconFeature.Boundary, XyiconFeature.Xyicon];

			const linkedModels = await getOrgSearchModelsByIds(appState, idsForDetails, false);

			for (const feature of features) {
				const newlyCreatedModelsForFeature = linkedModels.filter((lm) => lm.ownFeature === feature);
				if (newlyCreatedModelsForFeature.length > 0) {
					const filteredNewlyCreatedModels: IModel[] = [];

					for (const m of newlyCreatedModelsForFeature) {
						const modelId = m.id;
						const existingModelMaybe: IModel = appState.orgWideLists[feature].getById(modelId);
						if (existingModelMaybe) {
							existingModelMaybe.applyData(m.data);
						} else {
							filteredNewlyCreatedModels.push(m);
						}
					}

					if (filteredNewlyCreatedModels.length > 0) {
						appState.orgWideLists[feature].addMultiple(filteredNewlyCreatedModels);
					}
				}
			}
		}
	}

	return model;
};

export const getScopeWideSearchFeatures = (scopeWideSearchType: ScopeWideSearchType): XyiconFeature[] => {
	const ret = [
		XyiconFeature.Xyicon,
		XyiconFeature.Boundary,
		XyiconFeature.XyiconCatalog,
		XyiconFeature.Markup,
		XyiconFeature.Space,
		XyiconFeature.OrganizationDocument,
		XyiconFeature.PortfolioDocument,
	];

	if (scopeWideSearchType === "Organization") {
		ret.push(XyiconFeature.Portfolio);
	}

	return ArrayUtils.removeDuplicates(ret);
};

export const doesUserHavePermissionsToSpaceOfItem = (item: IModel, appState: AppState): boolean => {
	const supportedFeatures = [XyiconFeature.Xyicon, XyiconFeature.Boundary, XyiconFeature.Markup];

	if (supportedFeatures.includes(item.ownFeature)) {
		const spaceTypeIds = (
			item.ownFeature === XyiconFeature.Boundary
				? [...(item as Boundary).boundarySpaceMaps].map((bsm) => bsm.spaceTypeId)
				: [(item as Xyicon | Markup).spaceTypeId]
		).filter((stId: string) => !!stId);

		const userHasViewPermissionToAtLeastOneSpaceOfItem = spaceTypeIds.some(
			(spaceTypeId) => appState.actions.getModuleTypePermission(spaceTypeId, XyiconFeature.Space, item.portfolioId) >= Permission.View,
		);

		return userHasViewPermissionToAtLeastOneSpaceOfItem;
	}

	return false;
};

export const switchPortfolioAndRefreshSpacesIfNeeded = async (item: IModel, appState: AppState): Promise<void> => {
	// Note that it can be a Catalog, or Portfolio item, and we don't want to switch portfolios in those cases
	if (item.portfolioId && item.portfolioId !== appState.lastUsedPortfolioId) {
		const portfolioMaybe = appState.actions.getPortfolioById(item.portfolioId);
		if (portfolioMaybe) {
			appState.app.setState({isWaiting: true});
			const activePortfolioRefId = appState.activePortfolioRefId;
			if (activePortfolioRefId) {
				await appState.app.navigation.updatePortfolioRefIdInUrl(portfolioMaybe.refId);
			} else {
				// If we're at #app/<org-id>/home, there's no portfolioRefId in the URL, so we need to go to the new portfolio first
				appState.app.navigation.goApp({
					portfolioRefId: portfolioMaybe.refId,
					mainNav: NavigationEnum.SPACES,
				});
			}
			await appState.app.navigation.switchOrganizationAndPortfolioIfNecessary(appState.app.hash);
			appState.app.setState({isWaiting: false});
		}
		await appState.app.transport.services.feature.refreshList(XyiconFeature.Space);
	}
};

export const onLocateClick = async (
	item: Boundary | BoundarySpaceMap | Xyicon | Markup,
	appState: AppState,
	onShowBoundarySpaceMaps: (item: Boundary) => void,
): Promise<void> => {
	if (item.ownFeature === XyiconFeature.Boundary) {
		if (BoundaryUtils.doesHaveMultipleSpaceMaps(item)) {
			onShowBoundarySpaceMaps(item as Boundary);
			return;
		}
	}

	const {isScopeWideSearchOpen, setIsScopeWideSearchOpen} = useAppStore.getState();

	if (isScopeWideSearchOpen) {
		setIsScopeWideSearchOpen(false);
	}

	await switchPortfolioAndRefreshSpacesIfNeeded(item, appState);
	appState.app.onDetailsContainerClose(true);

	const spaceViewRenderer = appState.app.spaceViewRenderer;

	if (item.ownFeature === XyiconFeature.Markup) {
		const isMarkupOnMountedSpace = spaceViewRenderer.space?.id === item.spaceId;
		const markup3D = isMarkupOnMountedSpace
			? (spaceViewRenderer.markupManager.getItemById(item.id) as Markup3D)
			: spaceViewRenderer.markupManager.createSpaceItem3DFromModel(item);

		if (!markup3D.isVisible) {
			markup3D.showLayerWithNotification();
		}
	}

	await appState.actions.navigateToSpaceItem(item, true);
};
